// Worker API クライアント
// CLAUDE.md S5: CORS / S7: エラー詳細を表示しない

const API_BASE = import.meta.env.VITE_API_BASE || '';

async function call(path, init = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// AI 送信用に book を軽量化する。
// IndexedDB の book にはアプリ内生成書影の data URL（illustrationDataUrl、1冊で数MB）等が
// 含まれるため、そのまま送ると Worker の 1MB ボディ制限（S9）を超えて 413 になる。
// Worker 側ハンドラが参照するのは以下のフィールドのみ。
const AI_BOOK_FIELDS = ['id', 'name', 'author', 'status', 'startDate', 'endDate', 'rating', 'genre', 'note'];

function slimBook(book) {
  if (!book || typeof book !== 'object') return book;
  const out = {};
  for (const k of AI_BOOK_FIELDS) {
    if (book[k] !== undefined) out[k] = book[k];
  }
  return out;
}

const slimBooks = (books) => (Array.isArray(books) ? books.map(slimBook) : books);

// Notion 同期
export const fetchBooks = () => call('/api/sync/books');
export const fetchPageNote = (pageId) => call(`/api/sync/note?pageId=${encodeURIComponent(pageId)}`);
export const fetchCover = (title, author) =>
  call(`/api/cover?title=${encodeURIComponent(title)}&author=${encodeURIComponent(author || '')}`);

// AI
// refine: { feedback?: string, previousItems?: Array<{title,author}> }
export const recommendBooks = (books, count = 5, refine = {}) =>
  call('/api/recommend', {
    method: 'POST',
    body: JSON.stringify({
      books: slimBooks(books),
      count,
      feedback: refine.feedback || '',
      previousItems: refine.previousItems || [],
    }),
  });

export const searchByKeyword = (keyword, intent, books, count = 5, refine = {}) =>
  call('/api/search-by-keyword', {
    method: 'POST',
    body: JSON.stringify({
      keyword,
      intent,
      books: slimBooks(books),
      count,
      feedback: refine.feedback || '',
      previousItems: refine.previousItems || [],
    }),
  });

export const analyzeBook = (book, philosopher) =>
  call('/api/analyze', { method: 'POST', body: JSON.stringify({ book: slimBook(book), philosopher }) });

export const askFollowup = (book, philosopher, history, question) =>
  call('/api/ask', {
    method: 'POST',
    body: JSON.stringify({ book: slimBook(book), philosopher, history, question }),
  });

export const generateJournal = (yyyymm, books) =>
  call('/api/journal', { method: 'POST', body: JSON.stringify({ yyyymm, books: slimBooks(books) }) });

export const fetchThoughtMap = (books) =>
  call('/api/map', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

export const fetchLineage = (books) =>
  call('/api/lineage', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

export const fetchSerendipity = (books) =>
  call('/api/serendipity', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

export const fetchContrast = (book) =>
  call('/api/contrast', { method: 'POST', body: JSON.stringify({ book: slimBook(book) }) });

export const fetchCourse = (theme, level, count, books) =>
  call('/api/course', { method: 'POST', body: JSON.stringify({ theme, level, count, books: slimBooks(books) }) });

// 積読優先度提案
export const fetchTbrPriority = (tbr, history) =>
  call('/api/tbr-priority', { method: 'POST', body: JSON.stringify({ tbr: slimBooks(tbr), history: slimBooks(history) }) });

// 「次の問い」生成
export const fetchNextQuestion = (book) =>
  call('/api/next-question', { method: 'POST', body: JSON.stringify({ book: slimBook(book) }) });

// 「今日の問い」/「今日の引用」
export const fetchDailyPrompt = (books, date) =>
  call('/api/daily-prompt', { method: 'POST', body: JSON.stringify({ books: slimBooks(books), date }) });

// URL から書籍情報を抽出
export const extractBookFromUrl = (url) =>
  call('/api/extract-book-from-url', { method: 'POST', body: JSON.stringify({ url }) });

// 画像から書籍情報を抽出（Claude Vision）
export const extractBookFromImage = (imageBase64, mediaType) =>
  call('/api/extract-book-from-image', {
    method: 'POST',
    body: JSON.stringify({ imageBase64, mediaType }),
  });

// 最近の関心の総括
export const fetchRecentFocus = (books, days) =>
  call('/api/recent-focus', { method: 'POST', body: JSON.stringify({ books: slimBooks(books), days }) });

// 書籍間の対話 AI
export const fetchBookDialog = (books, topic) =>
  call('/api/book-dialog', { method: 'POST', body: JSON.stringify({ books: slimBooks(books), topic }) });

// 思考の癖メタ認知
export const fetchThoughtPatterns = (pairs) =>
  call('/api/thought-patterns', { method: 'POST', body: JSON.stringify({ pairs }) });

// 既読コーパス RAG チャット
export const fetchChatRag = (messages, context) =>
  call('/api/chat-rag', { method: 'POST', body: JSON.stringify({ messages, context }) });

// 書籍の「本人」AI 対話
export const fetchBookChat = (messages, book, context) =>
  call('/api/book-chat', { method: 'POST', body: JSON.stringify({ messages, book: slimBook(book), context }) });

// 写経の一節抽出
export const fetchShakyo = (books) =>
  call('/api/shakyo', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

// 過去メモの再会抜粋
export const fetchMemoEcho = (books) =>
  call('/api/memo-echo', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

// 季節・時節の一冊
export const fetchSeasonalPick = (books, season, time, dateLabel) =>
  call('/api/seasonal-pick', { method: 'POST', body: JSON.stringify({ books: slimBooks(books), season, time, dateLabel }) });

// 読書前の問いと読了後の振り返り
export const fetchPreReadingReflection = (book, question) =>
  call('/api/pre-reading-reflection', { method: 'POST', body: JSON.stringify({ book: slimBook(book), question }) });

// あなたの言葉辞典
export const fetchVocabulary = (books) =>
  call('/api/vocabulary', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

// 文体の経年変化
export const fetchStyleEvolution = (periods) =>
  call('/api/style-evolution', { method: 'POST', body: JSON.stringify({ periods }) });

// メモ間の影響関係
export const fetchMemoLineage = (books) =>
  call('/api/memo-lineage', { method: 'POST', body: JSON.stringify({ books: slimBooks(books) }) });

// 年報ポスター用テキスト生成
export const fetchYearReport = (books, label, periodSummary) =>
  call('/api/year-report', { method: 'POST', body: JSON.stringify({ books: slimBooks(books), label, periodSummary }) });

export const ping = () => call('/api/health');

// セマンティック検索用埋め込み生成（OpenAI text-embedding-3-small）
export const fetchEmbeddings = (texts) =>
  call('/api/embed', { method: 'POST', body: JSON.stringify({ texts }) });

// 全書籍のメモを並列取得（同期時 + AI 機能呼び出し前の補完用）
// concurrency=5 で Notion API のレート制限（3req/sec）には余裕を持って収まる
export async function fetchAllNotesParallel(books, onProgress) {
  const missing = books.filter((b) => !b.note);
  if (missing.length === 0) return books;
  const queue = [...missing];
  const total = missing.length;
  let done = 0;
  const concurrency = 5;
  const workers = Array.from({ length: concurrency }, async () => {
    while (queue.length) {
      const b = queue.shift();
      if (!b) break;
      try {
        const { note } = await fetchPageNote(b.id);
        if (note) b.note = note;
      } catch {}
      done += 1;
      if (onProgress) onProgress(done, total);
    }
  });
  await Promise.all(workers);
  return books;
}

// Notion ページにメモを追記
export const appendNotionNote = (pageId, text) =>
  call('/api/notion/append-note', { method: 'POST', body: JSON.stringify({ pageId, text }) });

// 書影イラストの静的パス。bookId は Notion ページ ID。
// Codex プラグイン経由で生成して /frontend/public/illustrations/<bookId>.png に配置される。
export function illustrationPathFor(bookId) {
  return `/illustrations/${bookId}.png`;
}

// 画像が存在するかを HEAD で確認（404 ならモノグラム fallback）
export async function illustrationExists(bookId) {
  try {
    const res = await fetch(illustrationPathFor(bookId), { method: 'HEAD' });
    return res.ok;
  } catch {
    return false;
  }
}

// 書影イラストを書籍テーマから AI 生成（PNG Blob を返す）
export async function requestIllustration({ title, author, theme }) {
  const res = await fetch(`${API_BASE}/api/illustrate-from-theme`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, author, theme }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `HTTP ${res.status}`);
  }
  return res.blob();
}
